import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { MdCheck, MdClose } from 'react-icons/md';
import { usePresentationBooking } from '../../../../hooks/usePresentationBooking';
import { PresentationTicketRegistrationPopulated, Slot, TrackingEvent } from '../../../../interfaces/interfaces';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { fetchPresentationSignedGuests } from '../../../../services/services';
import { VyExportButton } from '../../../common/VyExportButton';
import VyModal from '../../../common/VyModal';
import VyTooltip from '../../../common/VyTooltip';

interface PresentationGuestListProps {
  slot: Slot;
  color?: string; // color of the icon default is white
}

export const PresentationGuestList = (props: PresentationGuestListProps) => {
  const { slot, color = 'white' } = props;

  const [isModal, setIsModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tickets, setTickets] = useState<PresentationTicketRegistrationPopulated[]>([]);
  const [trackingEvents, setTrackingEvents] = useState<TrackingEvent[]>([]);

  const { getPresentationType } = usePresentationBooking();
  const event = useSelector((state) => state.admin.event ?? state.common.event);

  const { t } = useTranslation('account');

  const showGuestList = getPresentationType(slot) !== 'online' ? true : event?.settings?.enableOnlinePresentationBooking;

  useEffect(() => {
    if (showGuestList && isModal && !isLoading) fetch();
  }, [showGuestList, isModal]);

  async function fetch() {
    if (slot._id.includes('unassigned')) return;
    setIsLoading(true);
    try {
      const data = await fetchPresentationSignedGuests(slot._id);
      setTickets(data.tickets);
      setTrackingEvents(data.trackingEvents);
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }

  function hasParticipated(userId: string) {
    return trackingEvents.find((i) => i.userid === userId);
  }

  if (!showGuestList) return null;

  return (
    <>
      <VyTooltip title={t('account:slots.signed-guests-list')}>
        <FontAwesomeIcon
          style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}
          icon={faUsers}
          color={color}
          className="cursor-pointer"
          onClick={() => setIsModal(true)}
        />
      </VyTooltip>
      {isModal && (
        <VyModal maxWidth="90vw" open onClose={() => setIsModal(false)} hasDefaultPadding={false}>
          {() => (
            <div style={{ minWidth: '30vw' }} className="flex flex-col block">
              {isLoading && (
                <div className="text-center m-4">
                  <CircularProgress />
                </div>
              )}
              {!isLoading && tickets.length !== 0 && (
                <div>
                  <p className="text-center text-xl font-semibold	m-4">{t('account:slots.signed-guests-list')}</p>
                  <div className="flex justify-between w-full">
                    <div className="m-4">
                      Tickets: {tickets.length} {slot.meetingroom.presentationMaxSeats ? <>/ {slot.meetingroom.presentationMaxSeats}</> : <></>}
                      {slot.meetingroom.presentationSlotType === 'on-site' && (
                        <>
                          <br />
                          {t('common:participated')}: {tickets.filter((i) => hasParticipated(i.registration?.user?._id!)).length}/{tickets.length}
                        </>
                      )}
                    </div>
                    <VyExportButton
                      csvURL={`/presentationticket/signedguests/export/${slot._id}?resType=csv`}
                      xlsxURL={`/presentationticket/signedguests/export/${slot._id}?resType=xlsx`}
                      axiosMethod="GET"
                      className="mr-2"
                    />
                  </div>

                  <TableContainer className="mb-4">
                    <Table className="min-w-full" aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">{t('common:name')}</TableCell>
                          <TableCell align="center">{t('common:email')}</TableCell>
                          {slot.meetingroom.presentationSlotType === 'on-site' && <TableCell align="center">{t('common:participated')}</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tickets.map((ticket) => (
                          <TableRow key={ticket._id}>
                            <TableCell align="left">{ticket?.registration?.user?.name}</TableCell>
                            <TableCell align="center">{ticket?.registration?.user?.email}</TableCell>
                            {slot.meetingroom.presentationSlotType === 'on-site' && (
                              <TableCell align="center">
                                <VyTooltip title={t('account:slots.participated-tooltip')}>
                                  {ticket?.registration?.user?._id && hasParticipated(ticket?.registration?.user?._id) ? <MdCheck /> : <MdClose />}
                                </VyTooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
              {!isLoading && tickets.length === 0 && <Alert severity="info">{t('no-signed-users')}</Alert>}
            </div>
          )}
        </VyModal>
      )}
    </>
  );
};
